import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import { fromUnixTime, format } from 'date-fns';

import {
  Spinner,
  DateRangePicker,
  Button,
  Popover,
  PopoverButton,
  StyledPopoverPanel,
  PopoverItem,
  LoaderBalloon,
} from '@crazyegginc/hatch';

import { v4 as uuid } from 'uuid';

import { ReactComponent as DownloadIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-download.svg';
import { ReactComponent as ShareIcon } from '@crazyegginc/hatch/dist/images/icon-share-outline.svg';

import { DashboardPage } from '/src/components/Page';
import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { PaymentAlerts } from '/src/features/_global/components/PaymentAlerts';
import { DashboardPaywall } from '../../_global/paywalls/DashboardPaywall';
import { SitePickerBar } from '/src/components/site-picker/index';
import { NoMatchesWall } from '../../_global/paywalls/NoMatchesWall';
import { NotInstalledWall } from '../walls/NotInstalled';
import { NoSourcesWall } from '../walls/NoSources';
import { columnOrderAtom, getDefaultOrder, lastValidOrderAtom, searchAtom } from '../components/table';
import { DetailView } from './detail';
import { ListView } from './list';

import {
  webAnalyticsOverTimeQuery,
  webAnalyticsQuery,
  webAnalyticsConversionsOverTimeQuery,
  webAnalyticsDimensionQuery,
} from '../queries';
import {
  useAuthContext,
  useCanonicalDateRangeParam,
  useQueryParams,
  usePermissions,
  useNotifications,
  useHasFeatureFlag,
  useModal,
  useSite,
} from '/src/hooks';

import { FEATURES, MAX_ALLOWABLE_DATE_RANGES, SPECIAL_DATE_RANGES, TIMEZONES } from '/src/features/_global/constants';
import { WEB_ANALYTIC_METRICS, VALID_DIMENSION_VALUES, OTHERS } from '../constants';

import { getSourceFeatureStartedAt } from '../utils';
import { getFeatureName } from '/src/features/_global/utils';
import { toAtom } from '/src/utils/string';
import { ensureValidSearch } from '/src/utils/url';
import { scrollToLater } from '/src/utils/scroll';
import { bearerAuthHeader } from '/src/utils/auth';
import { JSON_DATE_RANGE_ADAPTER } from '/src/utils/date-range-adapters';

import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';
import { OptInWall } from '../walls/OptIn';
import { selectedGoalAtom } from '../components/goal-selector';
import { AskOwnerWall } from '../walls/AskOwner';
import { SOURCE_TYPE, SourceSelector, useSelectedSource } from '/src/components/source-selector/source-selector';
import { useSources } from '/src/hooks/useSources';
import { STATUS, usePageParams } from '/src/hooks/usePageParams';
import { ManageGaPropertiesModal } from '../../google-analytics/modals/ManageGaProperties';
import { DateRange } from '/src/components/date-time/DateRange';
import { meQuery } from '../../_global/queries';
import { DashboardBanner } from '/src/components/DashboardBanner';
import { SupportLinks } from '/src/support';
import {
  FIRST_VISIT_MODAL_FLOW,
  GA_FIRST_VISIT_TYPE,
  InstructionalModal,
} from '../components/modals/InstructionalModal';
import { SupportLink } from '/src/features/_global/components/SupportLink';
import { SHARABLE_RESOURCE_TYPES } from '../../team-and-sharing/constants';
import { DATE_QUERY_PARAM, SEMRUSH_DATE_QUERY_PARAM } from '/src/utils/queryParamAliases';

// eslint-disable-next-line no-restricted-syntax
export default function WebAnalyticsDashboard() {
  useSite();
  const modal = useModal();
  const { isSharing } = useAuthContext();
  const { allSelectableSources, loading: loadingSources, selectedSource } = useSources();
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.WEB_ANALYTICS);

  const pageParamsConfig = useMemo(
    () => [
      { key: 'metric', defaultValue: WEB_ANALYTIC_METRICS.UNIQUE_VISITORS, sync: true },
      { key: 'site' },
      { key: 'property' },
      { key: 'modal' },
    ],
    [],
  );

  const { status, params: pageParams } = usePageParams(pageParamsConfig);

  useEffect(() => {
    if (pageParams?.modal && pageParams.modal === 'manage_ga') {
      modal.show(<ManageGaPropertiesModal />);
    }
  }, [pageParams?.modal, modal]);

  const hasSource = Array.isArray(selectedSource) ? selectedSource.length > 0 : Boolean(selectedSource);

  const isFirstSource = !loadingSources && !allSelectableSources.length;

  if (!canViewDashboard) {
    return <DashboardPaywall reason={reason} feature={FEATURES.WEB_ANALYTICS} />;
  }

  const sourcesLoading = loadingSources;

  const renderPageContent = () => {
    if (!isSharing && isFirstSource) {
      return (
        <div className="p-5">
          <NoSourcesWall />
        </div>
      );
    }

    if (status === STATUS.READY && hasSource && !sourcesLoading) {
      return <WebAnalyticsContent fallbackMetric={pageParams?.metric} />;
    }

    return (
      <div className="mt-16 flex w-full items-center justify-center">
        <Spinner />
        <div className="ml-2.5">Loading...</div>
      </div>
    );
  };

  return (
    <DashboardPage>
      <SEO title="Web Analytics" />
      <DashHeader titleComponent={getFeatureName(FEATURES.WEB_ANALYTICS)} />
      {!isSharing ? <PaymentAlerts /> : null}
      {renderPageContent()}
    </DashboardPage>
  );
}

function WebAnalyticsContent({ fallbackMetric }) {
  const { currentAccount, isSharing } = useAuthContext();
  const permissions = usePermissions();
  const { sites, gaProperties, loading } = useSources();
  const { selectedSource } = useSelectedSource();
  const modal = useModal();

  const hasRestrictedSites = isSharing || (currentAccount.restrictedSitesIds || []).length > 0;

  const canSetUpAnalytics = permissions.can('edit', FEATURES.WEB_ANALYTICS).allowed;
  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;

  useEffect(() => {
    if (isSharing || loading || !selectedSource) {
      return;
    }

    const getStorageFlag = (key) => {
      try {
        return localStorage.getItem(key) === 'true';
      } catch {
        return false;
      }
    };

    const setStorageFlag = (key) => {
      try {
        localStorage.setItem(key, 'true');
      } catch {
        // no-op when localStorage is unavailable
      }
    };

    const isGaPropertySource = selectedSource.type === SOURCE_TYPE.GA_PROPERTY;
    const hasEnabledSuccessfullyConnectedGAProperty =
      currentAccount?.gaProperties?.some((prop) => !!prop.enabledAt && !!prop.lastSuccessAt) ?? false;

    const modalConditions = {
      [GA_FIRST_VISIT_TYPE.GA_INTRO]: () => !isGaPropertySource && (currentAccount?.gaProperties?.length ?? 0) === 0,
      [GA_FIRST_VISIT_TYPE.GA_PROPERTY_SWITCHER]: () =>
        (currentAccount?.gaProperties?.length ?? 0) > 0 && hasEnabledSuccessfullyConnectedGAProperty,
    };

    const showModalFromIndex = (index = 0) => {
      if (index >= FIRST_VISIT_MODAL_FLOW.length) {
        return;
      }

      const flowItem = FIRST_VISIT_MODAL_FLOW[index];
      const hasSeenModal = getStorageFlag(flowItem.storageKey);
      const shouldShowModal = modalConditions[flowItem.type]?.() ?? true;

      if (hasSeenModal || !shouldShowModal) {
        showModalFromIndex(index + 1);
        return;
      }

      modal.show(
        <InstructionalModal
          type={flowItem.type}
          onClose={() => {
            setStorageFlag(flowItem.storageKey);
            showModalFromIndex(index + 1);
          }}
          onConfirm={() => {
            if ([GA_FIRST_VISIT_TYPE.GA_PROPERTY_SWITCHER, GA_FIRST_VISIT_TYPE.GA_INTRO].includes(flowItem.type)) {
              window.dispatchEvent(new CustomEvent('openSourcePicker'));
            }
          }}
        />,
      );
    };

    showModalFromIndex();
  }, [selectedSource, modal, loading, currentAccount, isSharing]);

  if (!selectedSource) return null;

  const renderAnalyticsContent = () => {
    if (isSharing) {
      return <RenderAnalytics fallbackMetric={fallbackMetric} />;
    }

    if (selectedSource.type === SOURCE_TYPE.SITE) {
      if (!selectedSource.installed) {
        return <NotInstalledWall />;
      }

      if (!selectedSource.webAnalyticsEnabledAt) {
        return canSetUpAnalytics ? <OptInWall /> : <AskOwnerWall />;
      }

      return <RenderAnalytics fallbackMetric={fallbackMetric} />;
    }

    if (selectedSource.type === SOURCE_TYPE.GA_PROPERTY) {
      return <RenderAnalytics fallbackMetric={fallbackMetric} />;
    }

    return null;
  };

  return (
    <>
      <SitePickerBar
        Picker={SourceSelector}
        sites={sites}
        gaProperties={isSharing ? gaProperties : hasRestrictedSites ? [] : gaProperties}
        loading={!isSharing && loading}
        addSiteOption={canEditSites}
        enableGoogleAnalytics={!hasRestrictedSites}
        pageDisplayName="Web Analytics dashboard"
      />
      <GoogleAnalyticsSyncAlert />
      <div className="flex flex-col p-10 !pt-5">{renderAnalyticsContent()}</div>
    </>
  );
}

function GoogleAnalyticsSyncAlert() {
  const { selectedSource } = useSelectedSource();

  const [hasDismissedAlerts, setHasDismissedAlerts] = useState(
    () => window.sessionStorage.getItem('ce.dashboards.analytics.dismissedAlerts') ?? false,
  );

  function dismissAlerts() {
    window.sessionStorage.setItem('ce.dashboards.analytics.dismissedAlerts', 'true');
    setHasDismissedAlerts(true);
  }

  const hasError = !!selectedSource.lastError;

  if (hasDismissedAlerts || !hasError) {
    return null;
  }

  return (
    <DashboardBanner
      onClose={dismissAlerts}
      type="warning"
      title="We ran into a problem syncing data from Google Analytics into Crazy Egg"
    >
      We’re on it and will update your Web Analytics dashboard as soon as the data comes in. If the problem persists,{' '}
      <SupportLink className="text-link !text-white !underline" href={SupportLinks.general.newSupportRequest}>
        contact Support for help.
      </SupportLink>
    </DashboardBanner>
  );
}

const DEFAULT_LIMIT = 250;

function parseOrderDir(view, desc) {
  if (!view) return undefined;
  if (desc === true) return 'DESC';
  if (desc === false) return 'ASC';
  return undefined;
}

function parseOrderBy(view, field) {
  if (!view) return undefined;
  return toAtom(field);
}

// Only run queries in this component, we use the ones above
// to ensure the params are set and the site is loaded.
function RenderAnalytics({ fallbackMetric }) {
  const intervalRef = useRef(null);
  const takingLongRef = useRef(null);
  const permissions = usePermissions();
  const { token } = useAuthContext();
  const location = useLocation();
  const hasOverTime = useHasFeatureFlag('analytics-over-time');
  const canExportJson = useHasFeatureFlag('web-analytics-json-export');
  const canExportCsv = permissions.can('csv', FEATURES.EXPORT).allowed;
  const isDashboard = location.pathname.endsWith('analytics');
  const [pollState, setPollState] = useState({ id: null, status: 'off' });
  const [timeUnitOverride] = useState(null);
  const canShare = permissions.can('share', FEATURES.WEB_ANALYTICS).allowed;

  const { selectedSource } = useSelectedSource();

  // whether to show the export button
  const canExport = canExportJson || canExportCsv;

  const { get: paramGet, set: paramSet } = useQueryParams();
  const [order, setOrder] = useAtom(columnOrderAtom);
  const [selectedGoal] = useAtom(selectedGoalAtom);
  const [lastValidOrder, setLastValidOrder] = useAtom(lastValidOrderAtom);
  const [search, setSearch] = useAtom(searchAtom);
  const { view } = useParams();
  const navigate = useNavigate();
  const notifications = useNotifications();
  const sourceFeatureStartedAt = useMemo(() => getSourceFeatureStartedAt(selectedSource), [selectedSource]);
  const dateRangeResolverOptions = useMemo(
    () => ({
      featureStartedAt: sourceFeatureStartedAt,
    }),
    [sourceFeatureStartedAt],
  );

  const numSegments = Number(paramGet('num_segments', '10'));

  useEffect(() => {
    if (!view) return;
    if (!VALID_DIMENSION_VALUES.includes(view)) {
      notifications.error({
        id: 'invalid-dimension',
        content: 'Invalid dimension selected.',
        timeout: 2000,
        skipHoneybadger: true,
      });
      navigate(`/web-analytics${location.search}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const { resolvedDateRange } = useCanonicalDateRangeParam({
    timeZone: selectedSource?.currentTimezone ?? TIMEZONES.UTC,
    featureStartedAt: sourceFeatureStartedAt,
    maxAllowableRange: MAX_ALLOWABLE_DATE_RANGES.DEFAULT,
  });
  const dateRangeConfig = resolvedDateRange.selectedDateRange;
  const tzRange = resolvedDateRange.convertedDateRange;
  const predefinedRange = dateRangeConfig?.special ?? null;

  const orderColumnForOverTime = parseOrderBy(view, order?.id, false);

  const { refetch: fetchMe } = useQuery({
    ...meQuery(),
    enabled: false,
  });

  const pollPropertyReady = useCallback(async () => {
    if (!selectedSource?.id) return false;

    try {
      const res = await fetch(`${window.CORE_API_URL}/ga/${selectedSource.id}/check`, {
        headers: {
          ...bearerAuthHeader(token),
          'x-request-id': uuid(),
        },
      });
      if (!res.ok) return false;

      const text = (await res.text()).trim();
      if (!text) return false;

      if (text.toLowerCase() === 'ok') return true;

      try {
        const data = JSON.parse(text);
        const status = String(data?.status ?? data?.state ?? data?.result ?? '').toLowerCase();
        return status === 'ok';
      } catch {
        return false;
      }
    } catch {
      return false;
    }
  }, [selectedSource?.id, token]);

  const { data: rawOverTime, isLoading: overTimeLoading } = useQuery({
    ...webAnalyticsOverTimeQuery({
      dimension: view,
      metric: orderColumnForOverTime || lastValidOrder,
      sourceId: parseInt(selectedSource?.id) || null,
      sourceType: selectedSource?.type,
      startAt: tzRange.startDate,
      endAt: tzRange.endDate,
      numSegments: numSegments,
      goalId: selectedGoal?.id,
    }),
    refetchOnMount: 'always',
    enabled: hasOverTime && Boolean(selectedSource) && Boolean(view) && !isDashboard,
  });

  const { data: rawConversions, isLoading: _conversionsLoading } = useQuery({
    ...webAnalyticsConversionsOverTimeQuery({
      goalId: selectedGoal?.id,
      sourceId: parseInt(selectedSource?.id) || null,
      sourceType: selectedSource?.type,
      startAt: tzRange.startDate,
      endAt: tzRange.endDate,
    }),
    refetchOnMount: 'always',
    // enabled: hasOverTime && Boolean(selectedGoal?.id) && Boolean(selectedSource?.id) && isDashboard,
    enabled: false,
  });

  const canLoadData =
    (selectedSource?.type === SOURCE_TYPE.GA_PROPERTY && !!selectedSource?.lastSuccessAt) ||
    selectedSource?.type === SOURCE_TYPE.SITE;

  const {
    data: dashData,
    isLoading: dashLoading,
    hasNextPage: dashHasNextPage,
    fetchNextPage: dashFetchNextPage,
  } = useInfiniteQuery({
    ...webAnalyticsQuery({
      view: 'ALL',
      sourceId: parseInt(selectedSource?.id) || null,
      sourceType: selectedSource?.type,
      startAt: tzRange.startDate,
      endAt: tzRange.endDate,
      orderBy: parseOrderBy(view, order?.id),
      orderDir: parseOrderDir(view, order?.desc),
      limit: view ? DEFAULT_LIMIT : 25,
      granularity: timeUnitOverride || tzRange.timeUnit,
    }),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: ['polling', 'stillPolling'].includes(pollState.status) ? 60000 : false,
    enabled: canLoadData && isDashboard && Boolean(selectedSource) && Boolean(tzRange),
  });

  const {
    data: detailData,
    isLoading: detailLoading,
    hasNextPage: detailHasNextPage,
    fetchNextPage: detailFetchNextPage,
  } = useInfiniteQuery({
    ...webAnalyticsDimensionQuery({
      dimension: toAtom(view),
      sourceId: parseInt(selectedSource?.id) || null,
      sourceType: selectedSource?.type,
      startAt: tzRange.startDate,
      endAt: tzRange.endDate,
      searchTerm: search,
      orderBy: parseOrderBy(view, order?.id),
      orderDir: parseOrderDir(view, order?.desc),
      limit: view ? DEFAULT_LIMIT : 25,
    }),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: ['polling', 'stillPolling'].includes(pollState.status) ? 60000 : false,
    enabled: canLoadData && !isDashboard && Boolean(selectedSource) && Boolean(tzRange),
  });

  const data = isDashboard ? dashData : detailData;
  const isLoading = isDashboard ? dashLoading : detailLoading;
  const hasNextPage = isDashboard ? dashHasNextPage : detailHasNextPage;
  const fetchNextPage = isDashboard ? dashFetchNextPage : detailFetchNextPage;

  const pageData = useMemo(() => {
    if (!data?.pages) return null;

    if (isDashboard) {
      return data.pages?.[0]?.analytics ?? {};
    }

    let accData = data.pages.reduce((acc, page) => [...acc, ...(page?.analyticsDimension?.dimension?.data ?? [])], []);

    const lastPage = data.pages.at(-1)?.analyticsDimension ?? {};

    return {
      nextCursor: lastPage.nextCursor,
      dimension: {
        data: accData,
        totalCount: lastPage.dimension.totalCount,
      },
    };
  }, [data, isDashboard]);

  const webAnalyticsData = useMemo(() => {
    return {
      ...pageData,
      dimension: {
        ...(pageData?.dimension ?? {}),
      },
    };
  }, [pageData]);

  const overTimeData = useMemo(() => {
    const data = rawOverTime?.analyticsOverTime?.[view]?.overTime ?? [];

    const reordered = data.slice().sort((a, b) => {
      const isAOthers = a.name === OTHERS;
      const isBOthers = b.name === OTHERS;

      if (isAOthers && !isBOthers) return 1;
      if (!isAOthers && isBOthers) return -1;
      if ((isAOthers && isBOthers) || (!isAOthers && !isBOthers)) return 0;
    });

    return reordered;
  }, [rawOverTime?.analyticsOverTime, view]);

  const conversionsData = useMemo(() => {
    if (!view || view === 'ALL') {
      return rawConversions?.analyticsConversionsOverTime ?? [];
    }
    return rawOverTime?.analyticsOverTime?.[view]?.conversions ?? [];
  }, [rawOverTime?.analyticsOverTime, rawConversions?.analyticsConversionsOverTime, view]);

  const chooseDateRange = useCallback(
    (value) => {
      paramSet('date', JSON_DATE_RANGE_ADAPTER.serialize(value));
    },
    [paramSet],
  );

  const hasData = Object.keys(webAnalyticsData || {}).length > 0;

  useEffect(() => {
    const isGA = selectedSource.type === SOURCE_TYPE.GA_PROPERTY;

    // if not GA, force OFF, but only if needed
    if (!isGA) {
      setPollState((prev) => {
        const next = { id: selectedSource?.id, status: 'off' };
        return prev.id === next.id && prev.status === next.status ? prev : next;
      });
      return;
    }

    // if GA changed, reset.
    if (pollState.id !== selectedSource.id) {
      setPollState({ id: selectedSource.id, status: 'off' });
      return;
    }

    if (!selectedSource.lastSuccessAt) {
      // keep polling
      if (pollState.status !== 'polling' && pollState.status !== 'stillPolling') {
        setPollState({ id: selectedSource.id, status: 'polling' });
      }
      return;
    }

    if (['polling', 'stillPolling'].includes(pollState.status)) {
      // success
      setPollState({ id: selectedSource.id, status: 'transition' });
    }
  }, [pollState, selectedSource]);

  useEffect(() => {
    if (pollState.status !== 'transition') return;

    const timeoutId = setTimeout(() => {
      setPollState({ id: selectedSource.id, status: 'off' });
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [pollState, selectedSource.id]);

  useEffect(() => {
    const isPolling = ['polling', 'stillPolling'].includes(pollState.status) && pollState.id === selectedSource.id;

    if (!isPolling) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (takingLongRef.current) {
        clearTimeout(takingLongRef.current);
        takingLongRef.current = null;
      }
      return;
    }

    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = window.setInterval(async () => {
      const isReady = await pollPropertyReady();

      if (isReady) {
        await fetchMe();
      }
    }, 5000);

    if (pollState.status === 'polling') {
      if (takingLongRef.current) clearTimeout(takingLongRef.current);

      takingLongRef.current = window.setTimeout(() => {
        setPollState((prev) =>
          prev.id === selectedSource.id && prev.status === 'polling'
            ? { id: selectedSource.id, status: 'stillPolling' }
            : prev,
        );
      }, 60_000);
    }

    // cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (takingLongRef.current) {
        clearTimeout(takingLongRef.current);
        takingLongRef.current = null;
      }
    };
  }, [pollState.status, pollState.id, selectedSource.id, pollPropertyReady, fetchMe]);

  async function downloadResults() {
    if (!canExportJson) return;

    if (!webAnalyticsData || Object.keys(webAnalyticsData).length === 0) {
      return;
    }

    try {
      const date = new Date().toISOString().slice(0, 10);

      const jsonString = JSON.stringify(webAnalyticsData, null, 2);
      const resultsBlob = new Blob([jsonString], { type: 'application/json' });
      downloadBlob(resultsBlob, `web-analytics-${date}.json`);
    } catch (error) {
      notifications.error({
        id: 'analyticsExportError',
        timeout: 3000,
        content: `Failed to export your analytics data.`,
        context: { error: error instanceof Error ? error.message : String(error) },
      });
    }
  }

  const exportData = useCallback(() => {
    if (!canExportCsv) return;

    const exportPath = location.pathname.replaceAll('-', '_');
    const ext = isDashboard ? 'xlsx' : 'csv';

    const searchParams = new URLSearchParams(location.search);

    const params = new URLSearchParams({
      source_id: String(selectedSource?.id ?? ''),
      source_type: String(selectedSource?.type ?? ''),
      token,
    });

    if (predefinedRange && predefinedRange !== SPECIAL_DATE_RANGES.RECENT) {
      params.set('preset_date', predefinedRange);
    } else {
      params.set('start_at', tzRange.startDate);
      params.set('end_at', tzRange.endDate);
    }

    if (searchParams.get('metric')) params.set('metric', searchParams.get('metric'));
    if (searchParams.get('order')) params.set('order', searchParams.get('order'));
    if (searchParams.get('sort')) params.set('sort', searchParams.get('sort'));
    if (searchParams.get('search')) params.set('search', searchParams.get('search'));

    const [start, end] = [tzRange.startDate, tzRange.endDate].map((v) => {
      return format(fromUnixTime(v), 'yyyy-MM-dd');
    });

    const datePart = start !== end ? `${start}-to-${end}` : start;
    const filename = isDashboard ? `web-analytics-${datePart}.xlsx` : `${view}-${datePart}.csv`;
    params.set('filename', filename);

    const exportUrl = `${window.CORE_API_URL}/export${exportPath}.${ext}?${params.toString()}`;
    downloadFromUrl(exportUrl);
  }, [
    tzRange,
    predefinedRange,
    location.pathname,
    location.search,
    selectedSource?.id,
    selectedSource?.type,
    view,
    isDashboard,
    canExportCsv,
    token,
  ]);

  const showGaLoader =
    selectedSource?.type === SOURCE_TYPE.GA_PROPERTY &&
    (['polling', 'stillPolling', 'transition'].includes(pollState.status) || !selectedSource.lastSuccessAt);

  return (
    <>
      <div className="mx-auto flex w-full min-w-[940px] flex-col ">
        {view ? (
          <div>
            <Button
              variant="cancel"
              onClick={() => {
                setSearch('');
                setOrder(getDefaultOrder());
                setLastValidOrder(getDefaultOrder().id);
                navigate({
                  pathname: '/web-analytics',
                  search: ensureValidSearch(location.search, [
                    'source_id',
                    'source_type',
                    DATE_QUERY_PARAM,
                    SEMRUSH_DATE_QUERY_PARAM,
                    'metric',
                  ]),
                });
              }}
              className="!px-1 flex items-center space-x-1"
            >
              <ArrowIcon className="rotate-180 w-2.5 h-2.5 fill-current" />
              <span>Back</span>
            </Button>
          </div>
        ) : null}

        <div className="mb-2.5 flex justify-between items-center">
          <div className="ml-1 font-semibold text-lg text-black-pearl-500">
            <DateRange
              dateRange={dateRangeConfig}
              timeZone={selectedSource?.currentTimezone}
              resolverOptions={dateRangeResolverOptions}
            />
          </div>
          <div className="mr-1 flex items-center space-x-2">
            {canExport && !showGaLoader ? (
              <div className="flex justify-end w-full">
                <Popover className="relative" data-testid="question-menu">
                  {() => (
                    <>
                      <PopoverButton
                        as={Button}
                        className="text-cadet-blue-500 hover:text-dodger-blue-500 flex items-center space-x-2"
                        variant="secondary"
                      >
                        <DownloadIcon className="h-4 w-4 fill-current" />
                        <span>Export</span>
                      </PopoverButton>
                      <StyledPopoverPanel align="center" className="!w-[235px]">
                        {({ close }) => (
                          <>
                            {canExportCsv ? (
                              <PopoverItem
                                onClick={() => {
                                  close();
                                  exportData();
                                }}
                                className="group flex items-center"
                              >
                                {isDashboard ? `Export to spreadsheet (.xlsx)` : `Export to file (.csv)`}
                              </PopoverItem>
                            ) : null}
                            {canExportJson ? (
                              <PopoverItem
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  close();
                                  downloadResults();
                                }}
                                className="group flex items-center space-x-2"
                              >
                                <span>Export as JSON</span>
                              </PopoverItem>
                            ) : null}
                          </>
                        )}
                      </StyledPopoverPanel>
                    </>
                  )}
                </Popover>
              </div>
            ) : null}

            {canShare && !showGaLoader ? (
              <Button
                variant="secondary"
                onClick={() => {
                  if (!selectedSource) return;
                  window.SharingModal.show({
                    entity: {
                      id: parseInt(selectedSource.id),
                      siteId: parseInt(selectedSource.id),
                      type:
                        selectedSource.type === SOURCE_TYPE.GA_PROPERTY
                          ? SHARABLE_RESOURCE_TYPES.WEB_ANALYTICS_GA_PROPERTY
                          : SHARABLE_RESOURCE_TYPES.WEB_ANALYTICS_SITE,
                      shareLabel: 'Web Analytics source',
                    },
                  });
                }}
                className="flex items-center space-x-2"
              >
                <ShareIcon className="w-4 h-4 fill-current" />
                <span>Share</span>
              </Button>
            ) : null}

            {/* <div className="flex items-center"> */}
            {/*   {[ */}
            {/*     { label: 'Day', value: TIME_UNITS.DAY, className: '!rounded-none !rounded-l' }, */}
            {/*     { label: 'Week', value: TIME_UNITS.WEEK, className: '!rounded-none border-l border-r' }, */}
            {/*     { label: 'Month', value: TIME_UNITS.MONTH, className: '!rounded-none !rounded-r' }, */}
            {/*   ].map(({ label, value, className }) => ( */}
            {/*     <Button */}
            {/*       key={value} */}
            {/*       variant={timeUnitOverride === value ? undefined : 'secondary'} */}
            {/*       className={className} */}
            {/*       onClick={() => setTimeUnitOverride((current) => (current === value ? null : value))} */}
            {/*     > */}
            {/*       {label} */}
            {/*     </Button> */}
            {/*   ))} */}
            {/* </div> */}

            <DateRangePicker
              lowerBoundary={resolvedDateRange.pickerProps.lowerBoundary}
              upperBoundary={resolvedDateRange.pickerProps.upperBoundary}
              startDate={resolvedDateRange.pickerProps.startDate}
              endDate={resolvedDateRange.pickerProps.endDate}
              special={resolvedDateRange.pickerProps.special}
              setDateRange={chooseDateRange}
              setCommonDateRange={chooseDateRange}
              showCommonRanges={true}
              size="lg"
              maxRange={resolvedDateRange.pickerProps.maxRange}
              timezone={selectedSource?.currentTimezone ?? TIMEZONES.UTC}
              showTimeZone={true}
            />
          </div>
        </div>

        {showGaLoader ? (
          <div className="bg-white w-full h-[416px] p-10 rounded flex items-center justify-center flex-col shadow-md">
            <div className={pollState.status === 'stillPolling' ? 'max-w-[408px] text-center' : null}>
              <LoaderBalloon
                text={
                  pollState.status === 'polling'
                    ? 'Importing data from Google Analytics...'
                    : pollState.status === 'stillPolling'
                      ? 'Hang tight, this can take up to 5 to 10 minutes...'
                      : pollState.status === 'transition'
                        ? 'Creating your dashboard...'
                        : 'Loading...'
                }
              />
            </div>
          </div>
        ) : (
          <>
            {hasData || view || isLoading ? (
              view ? (
                <DetailView
                  isLoading={isLoading}
                  analytics={webAnalyticsData}
                  overTimeData={overTimeData}
                  conversionsData={conversionsData}
                  overTimeLoading={overTimeLoading}
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                />
              ) : (
                <ListView
                  fallbackMetric={fallbackMetric}
                  convertedDateRange={tzRange}
                  predefinedRange={predefinedRange}
                  isLoading={isLoading}
                  analytics={webAnalyticsData}
                  conversions={conversionsData}
                  timeUnitOverride={timeUnitOverride}
                />
              )
            ) : (
              <NoMatchesWall
                text={
                  <span>
                    data with the selected date range.
                    <br />
                    Try changing the date range
                  </span>
                }
              />
            )}

            {!view && hasData ? (
              <Button
                variant="cancel"
                className="justify-end w-full space-x-1.5 items-center mt-3.75 flex"
                onClick={() => scrollToLater(0)}
              >
                <ArrowIcon className="-rotate-90 fill-current w-2.5 h-2.5" />
                <span>Back to top</span>
              </Button>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadFromUrl(url) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
